import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SingAlongService } from '../services/SingAlongService';
import { CONTEST_INTENT_KEY } from '../services/ContestService';
import { useSingAlongBasePath } from '../hooks/useSingAlongBasePath';
import { TvFocusableInput } from '../components/TvFocusableInput';
import { Mail, Lock, ArrowRight, Loader as Loader2, CircleAlert as AlertCircle, ShieldCheck, ExternalLink, Check, UserPlus, CreditCard, Users, PartyPopper } from 'lucide-react';

const postAuthDest = (basePath: string) =>
  sessionStorage.getItem(CONTEST_INTENT_KEY) ? '/contests/plans' : `/${basePath}/plans`;

const STEPS = [
  { icon: UserPlus, label: 'Sign Up', desc: 'Create your host account' },
  { icon: CreditCard, label: 'Purchase Package', desc: 'Choose your contest plan' },
  { icon: Users, label: 'Invite Friends', desc: 'Share your contest link' },
  { icon: PartyPopper, label: 'Have a Blast!', desc: 'Sing, compete & celebrate' },
];

const TRY_IT_URL = 'https://thesingingleague.com/can-you-sing/test-corporate-kiosk';

export const SingAlongSignup: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSingAlongBasePath();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'signup' | 'login' | 'verify'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const urlCode = searchParams.get('code');
    const urlEmail = searchParams.get('email');
    if (urlCode && urlEmail) {
      setEmail(urlEmail);
      setCode(urlCode);
      setMode('verify');
      handleAutoVerify(urlEmail, urlCode);
      return;
    }

    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (user?.email_confirmed_at) {
        navigate(postAuthDest(basePath), { replace: true });
      } else if (user && !user.email_confirmed_at) {
        setEmail(user.email || '');
        setMode('verify');
      }
      setCheckingAuth(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAutoVerify = async (verifyEmail: string, verifyCode: string) => {
    try {
      await SingAlongService.verifyEmail(verifyEmail, verifyCode);
      const existing = await SingAlongService.getCurrentUser();
      if (existing?.email_confirmed_at) {
        setSuccess('Email verified! Redirecting...');
        setTimeout(() => navigate(postAuthDest(basePath), { replace: true }), 1500);
      } else {
        setEmail(verifyEmail);
        setMode('login');
        setSuccess('Email verified! Please sign in to continue.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await SingAlongService.signUp(email, password);
      const result = await SingAlongService.sendVerification(email, basePath);
      if (result.debug_code) setDebugCode(result.debug_code);
      setMode('verify');
    } catch (err: any) {
      if (err.message?.includes('already registered')) {
        setError('This email is already registered. Try logging in instead.');
      } else {
        setError(err.message || 'Signup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await SingAlongService.signIn(email, password);
      if (data.user?.email_confirmed_at) {
        navigate(postAuthDest(basePath), { replace: true });
      } else {
        const result = await SingAlongService.sendVerification(email, basePath);
        if (result.debug_code) setDebugCode(result.debug_code);
        setMode('verify');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await SingAlongService.verifyEmail(email, code);
      if (password) {
        await SingAlongService.signIn(email, password);
        setSuccess('Email verified! Redirecting...');
        setTimeout(() => navigate(postAuthDest(basePath), { replace: true }), 1200);
      } else {
        setMode('login');
        setSuccess('Email verified! Please sign in to continue.');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await SingAlongService.resendVerification(email, basePath);
      if (result.debug_code) setDebugCode(result.debug_code);
      setSuccess('Verification email sent! Check your inbox.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend');
    } finally {
      setLoading(false);
    }
  };

  const currentStepIdx = mode === 'verify' ? 0 : 0;

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-slate-950" />
      <div className="absolute inset-0" style={{
        backgroundImage: [
          'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(234,88,12,0.12), transparent 60%)',
          'radial-gradient(ellipse 60% 80% at 80% 20%, rgba(251,191,36,0.08), transparent 50%)',
          'radial-gradient(ellipse 70% 50% at 60% 90%, rgba(14,165,233,0.06), transparent 50%)',
        ].join(','),
      }} />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
        backgroundSize: '40px 40px',
      }} />

      {/* Left Panel - Desktop */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-950/40 via-slate-950/80 to-slate-950" />
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/[0.06] to-transparent" />

        <div className={`relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full transition-all duration-1000 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          {/* Logo */}
          <Link to={`/${basePath}`} className="flex items-center gap-3 group">
            <img
              src="/TSL_Logo.png"
              alt="The Singing League"
              className="h-14 w-14 object-contain group-hover:scale-105 transition-transform"
            />
            <span className="text-white font-bold text-lg tracking-tight">The Singing League</span>
          </Link>

          {/* Middle content: headline + stepper */}
          <div className="flex-1 flex flex-col justify-center py-12">
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-3">
              Host your contest
              <span className="block mt-1 bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">in minutes.</span>
            </h2>
            <p className="text-white/45 text-base leading-relaxed mb-10 max-w-sm">
              Set up a sing-along competition for your next event. AI scoring, live leaderboards, and instant results.
            </p>

            {/* Vertical stepper */}
            <div className="space-y-0">
              {STEPS.map((step, idx) => {
                const isActive = idx === currentStepIdx;
                const isComplete = idx < currentStepIdx;
                const Icon = step.icon;
                return (
                  <div key={step.label} className="flex items-start gap-4">
                    {/* Connector line + circle */}
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all duration-500 ${
                        isActive
                          ? 'bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/30'
                          : isComplete
                          ? 'bg-emerald-500/20 border border-emerald-500/40'
                          : 'bg-white/[0.04] border border-white/[0.08]'
                      }`}>
                        {isComplete ? (
                          <Check size={16} className="text-emerald-400" />
                        ) : (
                          <Icon size={16} className={isActive ? 'text-white' : 'text-white/30'} />
                        )}
                      </div>
                      {idx < STEPS.length - 1 && (
                        <div className={`w-px h-10 my-1 transition-colors ${
                          idx < currentStepIdx ? 'bg-emerald-500/40' : 'bg-white/[0.06]'
                        }`} />
                      )}
                    </div>
                    {/* Text */}
                    <div className="pt-2 pb-4">
                      <p className={`font-semibold text-sm transition-colors ${
                        isActive ? 'text-white' : isComplete ? 'text-emerald-400' : 'text-white/35'
                      }`}>
                        {step.label}
                      </p>
                      <p className={`text-xs mt-0.5 transition-colors ${
                        isActive ? 'text-white/60' : 'text-white/20'
                      }`}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom: Try it out link */}
          <div className="pt-4">
            <a
              href={TRY_IT_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-orange-400 transition-colors group"
            >
              <ExternalLink size={14} className="group-hover:scale-110 transition-transform" />
              <span>On the fence? Try a free demo first</span>
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col items-center justify-center relative px-5 sm:px-8 py-10 lg:py-0">
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/[0.06] rounded-full blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-amber-500/[0.04] rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        <div className={`relative z-10 w-full max-w-md transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {/* Mobile: Logo + compact stepper */}
          <div className="lg:hidden mb-8">
            <Link to={`/${basePath}`} className="flex items-center gap-3 mb-6">
              <img src="/TSL_Logo.png" alt="The Singing League" className="h-10 w-10 object-contain" />
              <span className="text-white font-bold text-lg">The Singing League</span>
            </Link>

            {/* Horizontal mini-stepper */}
            <div className="flex items-center gap-1 mb-2">
              {STEPS.map((step, idx) => (
                <React.Fragment key={step.label}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    idx === currentStepIdx
                      ? 'bg-gradient-to-br from-orange-500 to-amber-500 text-white'
                      : idx < currentStepIdx
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-white/[0.04] text-white/30 border border-white/[0.08]'
                  }`}>
                    {idx < currentStepIdx ? <Check size={12} /> : idx + 1}
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-px ${idx < currentStepIdx ? 'bg-emerald-500/40' : 'bg-white/[0.06]'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex justify-between px-0.5">
              {STEPS.map((step, idx) => (
                <span key={step.label} className={`text-[10px] ${idx === currentStepIdx ? 'text-orange-400 font-medium' : 'text-white/25'}`}>
                  {step.label}
                </span>
              ))}
            </div>
          </div>

          {/* Form header */}
          <div className={`mb-7 transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
              {mode === 'verify' ? 'Verify Your Email' : mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-white/45 text-sm">
              {mode === 'verify'
                ? 'Enter the 6-digit code we sent to your email'
                : mode === 'login'
                ? 'Sign in to manage your sing-along'
                : 'Sign up to host your own sing-along contest'}
            </p>
          </div>

          {/* Form card */}
          <div className={`bg-white/[0.025] backdrop-blur-sm border border-white/[0.07] rounded-2xl p-7 sm:p-8 transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {mode === 'verify' ? (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="text-center">
                  <div className="w-14 h-14 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck size={28} className="text-orange-400" />
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    We sent a verification email to{' '}
                    <span className="font-semibold text-white">{email}</span>.
                    Click the link or enter the code below.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Verification Code</label>
                  <TvFocusableInput
                    type="text"
                    inputMode="numeric"
                    enterKeyHint="done"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-full px-4 py-4 bg-white/[0.04] border border-white/[0.1] rounded-xl text-white text-center text-2xl font-mono tracking-[0.5em] placeholder-white/20 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 transition"
                    placeholder="000000"
                    autoFocus
                  />
                </div>

                {debugCode && (
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
                    <p className="text-xs text-blue-300 mb-1">Dev mode (no email service configured):</p>
                    <p className="text-lg font-mono font-bold text-blue-200 tracking-widest">{debugCode}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-orange-500/20 transition-all disabled:opacity-50 disabled:hover:shadow-none flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <>
                      Verify Email
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="w-full py-3 bg-white/[0.04] border border-white/[0.1] text-white/60 font-semibold rounded-xl hover:bg-white/[0.07] hover:text-white/80 transition-all disabled:opacity-50"
                >
                  Resend Verification Email
                </button>
              </form>
            ) : (
              <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 z-10 pointer-events-none" />
                    <TvFocusableInput
                      type="email"
                      enterKeyHint="next"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3.5 bg-white/[0.04] border border-white/[0.1] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 focus:bg-white/[0.06] transition-all text-sm"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 z-10 pointer-events-none" />
                    <TvFocusableInput
                      type="password"
                      enterKeyHint="go"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full pl-10 pr-4 py-3.5 bg-white/[0.04] border border-white/[0.1] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 focus:bg-white/[0.06] transition-all text-sm"
                      placeholder="Min 6 characters"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-orange-500/20 transition-all disabled:opacity-50 disabled:hover:shadow-none flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <>
                      {mode === 'login' ? 'Sign In' : 'Create Account'}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(null); }}
                    className="text-sm text-orange-400/80 hover:text-orange-300 transition-colors"
                  >
                    {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                  </button>
                </div>
              </form>
            )}

            {error && (
              <div className="mt-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-red-500/[0.08] border border-red-500/15">
                <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-300 leading-relaxed">{error}</p>
              </div>
            )}

            {success && (
              <div className="mt-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-green-500/[0.08] border border-green-500/15">
                <ShieldCheck size={16} className="text-green-400 shrink-0 mt-0.5" />
                <p className="text-sm text-green-300 leading-relaxed">{success}</p>
              </div>
            )}
          </div>

          {/* Mobile: Try it out link */}
          <div className="lg:hidden mt-6 text-center">
            <a
              href={TRY_IT_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-white/35 hover:text-orange-400 transition-colors"
            >
              <ExternalLink size={13} />
              <span>On the fence? Try a free demo first</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingAlongSignup;
